import { DatePipe, NgClass } from "@angular/common";
import {
    Component,
    Input,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { RouterLink } from "@angular/router";
import { IconButtonComponent } from "@components/icon-button/icon-button.component";
import { ListColumnDirective } from "@components/list/list-column.directive";
import { ListComponent } from "@components/list/list.component";
import { NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { chain } from "lodash";
import { Subject, forkJoin } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ToastService } from "~app/common/components/toast/toast.service";
import { ConfirmDialogResponse, RaskRoller, VirkeskopKontraktstatus } from "~app/common/models/constants";
import { SearchKontraktResponse } from "~app/common/models/virkeskop/v2/SearchKontraktResponse";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { FeatureService } from "~app/common/services/feature.service";
import { CommonTexts } from "~app/features/virkeskop/common/common-texts";
import { IAtgardstypWithGroup } from "../../models/atgardstyp-with-group-dto";
import { VirkeskopKontraktstyp } from "../../models/kontrakt-models";
import { SkogsvardService } from "../../services/skogsvard.service";
import { VirkeskopService } from "../../services/virkeskop.service";
import { IAtgardstypGruppTooltip, IAtgardstypTooltip, IVKSearchResultWithFilter } from "../models/kontrakt-search-filter-model";
import { AnbudKontraktSearchService } from "../services/anbud-kontrakt-search.service";

@Component({
    selector: "rask-anbud-kontrakt-list",
    templateUrl: "./anbud-kontrakt-list.component.html",
    styleUrls: ["./anbud-kontrakt-list.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ListComponent,
        ListColumnDirective,
        RouterLink,
        NgClass,
        NgbTooltip,
        IconButtonComponent,
        DatePipe
    ]
})
export class AnbudKontraktListComponent implements OnInit, OnDestroy {
    @Input() isSkogsvardFPV = false;

    protected asyncDataLoaded = false;
    protected isKopledaOrVirkeskopa = false;
    protected searchResult: IVKSearchResultWithFilter = {
        filter: null,
        result: []
    };
    protected statusKontrakt = VirkeskopKontraktstatus.Kontrakt;
    protected texts = CommonTexts.texts;
    private readonly anbudKontraktSearchService = inject(AnbudKontraktSearchService);
    private readonly authService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly featureService = inject(FeatureService);
    private readonly modalDialogService = inject(RaskModalService);
    private readonly skogsvardService = inject(SkogsvardService);
    private readonly toastService = inject(ToastService);
    private readonly unsubscribe$ = new Subject();
    private readonly virkeskopService = inject(VirkeskopService);

    private indexedAtgardGroupsForKortnamn: Map<string, string>;
    private indexedAtgardtypNamn: Map<string, string>;
    private orderedAtgardGroupsWithAtgardstyper: Map<string, string[]>;

    ngOnDestroy(): void {
        this.anbudKontraktSearchService.clearResults();
        this.unsubscribe$.unsubscribe();
    }

    ngOnInit(): void {
        this.isKopledaOrVirkeskopa = this.authService.hasRoles(`${RaskRoller.role.Kopleda},${RaskRoller.role.Virkeskopa}`, false);

        if (this.isSkogsvardFPV) {
            forkJoin([this.skogsvardService.getSkogsvardsTyperWithGroups()]).subscribe(([skogsvardstyperWithGroups]) => {
                this.handleAtgardstypergroups(skogsvardstyperWithGroups);
                this.asyncDataLoaded = true;
            });
        } else {
            forkJoin([
                this.virkeskopService.getAtgardsTyperWithGroups(),
                this.skogsvardService.getSkogsvardsTyperWithGroups()
            ]).subscribe(([
                atgardstyperWithGroups,
                skogsvardstyperWithGroups
            ]) => {
                const typerWithGroups: IAtgardstypWithGroup[] = atgardstyperWithGroups.concat(skogsvardstyperWithGroups);
                this.handleAtgardstypergroups(typerWithGroups);
                this.asyncDataLoaded = true;
            });
        }

        this.anbudKontraktSearchService.searchResult$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(x => {
                this.searchResult = x;
                this.searchResult.result.forEach(item => {
                    item.atgardstypSingleLine = this.getFormattedAtgardsTyperOneLine(item);
                    item.atgardstypMultiLine = this.getFormattedAtgardsTyperMultiLine(item);
                });
            });
    }

    /**
     * Bygger upp en trädstruktur med åtgärdsgrupp > åtgärdstyp baserat på åtgärdstyper som finns i kontraktet
     * Använder sorteringen som finns på orderedAtgardGroupsWithAtgardstyper
     */
    protected getFormattedAtgardsTyperMultiLine = (row: SearchKontraktResponse): IAtgardstypGruppTooltip[] => {
        if (!row?.atgardsTyper?.length) {
            return [];
        }

        const resultingTypeNames = row.atgardsTyper.map(x => this.indexedAtgardtypNamn.get(x));
        const filteredAtgardTypeNames = this.searchResult.filter.atgardstyp.concat(this.searchResult.filter.skogsvardstyp);
        const filteredTypeNames = filteredAtgardTypeNames.map(x => this.indexedAtgardtypNamn.get(x));

        const resultingGroups = chain(row.atgardsTyper)
            .map(x => this.indexedAtgardGroupsForKortnamn.get(x))
            .uniq()
            .value();

        const mapTypeToTooltip = (name: string) => {
            const count = resultingTypeNames.filter(y => y === name).length;
            const selected = filteredTypeNames.includes(name);

            return {
                name,
                count,
                selected
            } as IAtgardstypTooltip;
        };

        const mapGroupWithTypesToTooltip = (name: string, types: string[]) => {
            const typer = types
                .filter((typ: string) => resultingTypeNames.includes(typ))
                .map(mapTypeToTooltip);

            return {
                name,
                typer
            } as IAtgardstypGruppTooltip;
        };

        const orderedGroupTypes: [string, string[]][] = Array.from(this.orderedAtgardGroupsWithAtgardstyper.entries());
        return chain(orderedGroupTypes)
            .filter(([
                group,
                _types
            ]) => resultingGroups.includes(group))
            .map(([
                group,
                types
            ]) => mapGroupWithTypesToTooltip(group, types))
            .value();
    };

    protected getFormattedAtgardsTyperOneLine = (row: SearchKontraktResponse): string => {
        if (!row || !row.atgardsTyper?.length) {
            return "-";
        }

        const filteredAtgardtypsGroups = this.searchResult.filter.atgardstyp.concat(this.searchResult.filter.skogsvardstyp);
        const filteredGroups = filteredAtgardtypsGroups.map(x => this.indexedAtgardGroupsForKortnamn.get(x));

        return chain(row.atgardsTyper)
            .map(typkortnamn => this.indexedAtgardGroupsForKortnamn.get(typkortnamn))
            .uniq()
            .sortBy(
                (gruppnamn) => filteredGroups.indexOf(gruppnamn) >= 0 ? 0 : 1, // filtrerade gruppnamn hamnar först
                (gruppnamn) => gruppnamn) // bokstavsordning
            .value()
            .join(", ");
    };

    protected getViolVersion(row: SearchKontraktResponse): string {
        return row.viol3 ? "Viol 3" : "Viol 2";
    }

    protected onMakulera = (row: SearchKontraktResponse) => {
        const kontraktId = row.kontraktId;
        const kontraktnummer = row.kontraktnummer;
        const confirmText = row.typ === VirkeskopKontraktstyp.SkogsvardFPV ?
            `${this.texts.makuleraKontraktConfirmFPV} ${kontraktnummer}?` :
            `${this.texts.makuleraKontraktConfirm} ${kontraktnummer}?`;
        const settings = new ModalSettings(this.texts.makuleraKontrakt, confirmText);
        settings.contentIsMarkup = true;
        const confirmDialog = this.modalDialogService.confirm(settings);
        confirmDialog.subscribe((response) => {
            if (response === ConfirmDialogResponse.cancel) {
                return;
            }

            this.makulera(kontraktId);
        });
    };

    /**
     * Bygger upp index för åtgärdstyper för att snabbt kunna slå upp informationen
     * Behöver göra index för att:
     * - Åtgärdstyp-kolumnen renderas för varje rad
     * - Tooltip för åtgärdstyp renderas för varje rad
     * - En sortering på ett ställe
     * - Inte suga för mycket CPU
     *
     * indexedAtgardGroupsForKortnamn = En Map som är baserad på atgardstyp.kortnamn > atgardstyp.grupp
     * indexedAtgardtypNamn = En Map som är baserad på atgardstyp.kortnamn > atgardstyp.namn
     * orderedAtgardGroupsWithAtgardstyper = En Map som är baserad på atgardstyp.grupp > alla åtgärdstyper för denna grupp
     *
     * Sorteringen behålls genom att Map är garanterad att behålla sorteringen då när den itereras så gör den det i insert-order.
     *
     */
    private handleAtgardstypergroups(atgardstyperWithGroups: IAtgardstypWithGroup[]) {
        const groupsWithTyper = chain(atgardstyperWithGroups)
            .orderBy([
                x => x.grupp,
                x => x.namn
            ])
            .groupBy(x => x.grupp)
            .map((val, key) => [
                key,
                val.map(x => x.namn)
            ] as [string, string[]])
            .value();

        this.orderedAtgardGroupsWithAtgardstyper = new Map<string, string[]>(groupsWithTyper);

        const typerToGroups = chain(atgardstyperWithGroups)
            .map(x => [
                x.kortnamn,
                x.grupp
            ] as [string, string])
            .value();

        this.indexedAtgardGroupsForKortnamn = new Map<string, string>(typerToGroups);

        const atgardstypNamn = chain(atgardstyperWithGroups)
            .map(x => [
                x.kortnamn,
                x.namn
            ] as [string, string])
            .value();

        this.indexedAtgardtypNamn = new Map<string, string>(atgardstypNamn);
    }

    private readonly makulera = (kontraktId: number) => {
        const observable = this.busyService.addWork(this.virkeskopService.makulera(kontraktId));
        observable.subscribe(() => {
            const kontrakt = this.searchResult.result.find(k => k.kontraktId === kontraktId);
            if (kontrakt) {
                kontrakt.kontraktstatus = VirkeskopKontraktstatus.Makulerat;
                kontrakt.senastandrad = new Date().toLocaleDateString();
            }

            this.toastService.add(this.texts.kontraktetHarMakulerats);
        });
    };
}
